export interface FaqPortableTextBlock {
  _type?: string;
  children?: Array<{ text?: string }>;
}

export interface FaqItem {
  question: string;
  answer: FaqPortableTextBlock[];
  order?: number;
}

export function extractFaqPlainText(blocks?: FaqPortableTextBlock[]): string {
  if (!blocks?.length) return "";

  return blocks
    .map((block) => {
      if (block._type === "block" && Array.isArray(block.children)) {
        return block.children.map((child) => child.text || "").join("");
      }
      return "";
    })
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

export interface FaqPageSchema {
  "@context": "https://schema.org";
  "@type": "FAQPage";
  mainEntity: Array<{
    "@type": "Question";
    name: string;
    acceptedAnswer: {
      "@type": "Answer";
      text: string;
    };
  }>;
  datePublished?: string;
  speakable?: {
    "@type": "SpeakableSpecification";
    cssSelector: string[];
  };
}

export function buildFaqPageSchema(
  faqs: FaqItem[],
  options?: { datePublished?: string },
): FaqPageSchema | null {
  const mainEntity = [...faqs]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((faq) => {
      const answerText = extractFaqPlainText(faq.answer);
      if (!faq.question?.trim() || !answerText) return null;

      return {
        "@type": "Question" as const,
        name: faq.question.trim(),
        acceptedAnswer: {
          "@type": "Answer" as const,
          text: answerText,
        },
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  if (mainEntity.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    ...(options?.datePublished
      ? { datePublished: options.datePublished }
      : {}),
    mainEntity,
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: [".faq-question", ".faq-answer"],
    },
  };
}
